import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RequestThread = ({ booking }) => {
  const { t } = useTranslation();
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/bookings/${booking.booking_id}/messages`)
      .then(({ data }) => setMsgs(data.messages || []))
      .catch(() => setMsgs([]));
  }, [booking.booking_id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [msgs]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.booking_id}/messages`, { body: text });
      setMsgs((m) => [...(m || []), data]);
      setBody("");
    } catch {
      toast.error(t("vc.acct.thread.fail"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/[0.06] pt-4" data-testid={`thread-${booking.booking_id}`}>
      {/* original request reads as the first message */}
      <div className="space-y-2.5 sm:max-h-72 sm:overflow-y-auto no-scrollbar sm:pr-1">
        <Bubble side="member" name={t("vc.acct.thread.you")} at={booking.created_at} body={booking.message} />
        {msgs === null ? (
          <div className="flex items-center gap-2 text-neutral-500 text-[12px]">
            <Loader2 size={13} className="animate-spin" /> {t("common.loading")}
          </div>
        ) : (
          msgs.map((m) => (
            <Bubble
              key={m.message_id}
              side={m.author}
              name={m.author === "concierge" ? t("vc.acct.thread.concierge") : t("vc.acct.thread.you")}
              at={m.created_at}
              body={m.body}
            />
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-3 flex items-end gap-2">
        <textarea
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("vc.acct.thread.ph")}
          data-testid={`thread-input-${booking.booking_id}`}
          className="flex-1 bg-transparent border border-white/[0.12] rounded-2xl focus:border-white/45 outline-none py-2.5 px-4 text-[13px] font-light resize-none transition-colors placeholder:text-neutral-600"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          data-testid={`thread-send-${booking.booking_id}`}
          className="shrink-0 inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-40 transition-colors"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          {t("vc.acct.thread.send")}
        </button>
      </div>
    </div>
  );
};

const Bubble = ({ side, name, at, body }) => {
  const mine = side === "member";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${mine ? "bg-white text-black" : "bg-white/[0.05] text-neutral-200 border border-white/[0.08]"}`}>
        <div className={`text-[8px] tracking-[0.25em] uppercase ${mine ? "text-neutral-600" : "text-neutral-500"}`}>
          {name} · {new Date(at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
        <p className="text-[13px] font-light leading-relaxed mt-1 whitespace-pre-wrap">{body}</p>
      </div>
    </div>
  );
};

export const ThreadToggle = ({ open, onClick, testid, label }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-neutral-400 hover:text-white transition-colors"
  >
    {label}
    <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
  </button>
);
